import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Header, SearchFilters, expandIcon } from 'datastake-daf/dist/components';
import {
  filtersConfig,
  checkboxConfig,
  columnsConfig,
  selectFiltersConfig as defaultSelectFiltersConfig,
} from './config';
import Settings from '../components/Settings';
import Loading from '../../../components/Loading';
import DafTable from '../../../components/Table/newTable';
import DataStoreService from '../../../services/DataStore';
import { useFilters } from '../../../helpers/SubjectSearch';
import Styles from '../../../components/Forms/locations/style';
import { renderBreadCrumbs } from '../../../helpers/breadCrumbs';
import TablePagination from '../../../components/Table/pagination';
import { renderRows } from '../../Analysis/sca-dds-kyc-widgets/table-components/helper';
import { useSelector } from 'react-redux';
import { getType } from '../helper';

export default function DataStoreTable({ mod }) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const loadingKeys = useRef({});
  const { namespace } = useParams();
  const [expanded, setExpanded] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [toggledKeys, setToggledKeys] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectOptions] = useState({});
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const options = useSelector((state) => state.authentication.options);

  const columns = useMemo(() => columnsConfig({
    t,
    goTo,
    namespace,
    mod,
    setSettingsVisible,
    options,
  }), [t, goTo, namespace, mod, options]);

  const view = useMemo(() => `data-store/${namespace}`, [namespace])

  const type = useMemo(() => getType(namespace), [namespace]);

  const breadCrumbs = useMemo(() => {
    return renderBreadCrumbs({ t, view: namespace, isDataStore: true, goTo, mod });
  }, [t, goTo, namespace, mod]);

  const label = useMemo(() => {
    switch (namespace) {
      case 'events':
        return t('Events');
      case 'archives':
        return t('Archives');
      case 'locations':
        return t('Locations');
      case 'entities':
        return t('Entities');
      default:
        return '';
    }
  }, [namespace, t]);

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
    setPagination,
  } = useFilters({
    selectFiltersConfig: defaultSelectFiltersConfig,
    view,
    module: mod,
    filtersConfig,
  });

  const fetch = async () => {
    setLoading(true);

    try {
      const { page, pageSize, ...rest } = activeFilters;
      const params = {
        pagination: { page, perPage: pageSize },
        ...rest
      };
      const { data } = await DataStoreService.getSubjects(type, params)
      setPagination((prev) => ({ ...prev, total: data?.metadata[0]?.total }))
      setData(data.data);
    } catch (err) {
      console.log(err)
    }

    setLoading(false);
  };

  useEffect(() => {
    fetch();
  }, [activeFilters]);

  const onFinish = async (formValue) => {
    setLoading(true);

    try {
      await DataStoreService.saveSubjectSettings(settingsVisible?.id, formValue);
      await fetch();
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  const toggle = useCallback((key) => setExpanded((prev) => [...prev, key]), []);
  const unToggle = useCallback((key) => setExpanded((prev) => [...prev.filter((p) => p !== key)]), []);

  const toggleExpand = useCallback((expanded, { key }) =>
    expanded ? toggle(key) : unToggle(key), [toggle, unToggle]);

  return (
    <Styles className="semibold form-input-output">
      {loading && <Loading />}

      <Header
        title={label}
        breadcrumbs={breadCrumbs}
      />

      <div className="column start table-content w-support-text">
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            canClear={canClearSearch}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={data}
            loading={loading}
            pagination={pagination}
            rowKey="key"
            doEmptyRows
            selectOptions={selectOptions}
            filtersConfig={defaultSelectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            defaultFilters={defaultFilters}
            setShowFilters={setShowFilters}
            expandable={{
              onExpand: toggleExpand,
              rowExpandable: (a) => {
                return Array.isArray(a.versions);
              },
              expandedRowRender: (d) => renderRows({
                data: d,
                columns,
                expanded: toggledKeys,
                dataKey: 'versions',
              }),
              expandedRowKeys: expanded,
              expandedRowClassName: () => 'expanded-table-cell'
            }}
            expandIcon={(props) => expandIcon({
              ...props,
              arrows: true,
              toggledKeys,
              setToggledKeys,
              loadingKeys,
            })}
            onChange={onTableChange}
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </div>

      {!!settingsVisible && (
        <Settings
          defaultData={{
            discoverability: settingsVisible.discoverability,
            accessibility: settingsVisible.accessibility,
            history: settingsVisible.history,
            stream: settingsVisible.stream,
            delivery: settingsVisible.delivery,
            licensing: settingsVisible.licensing,
          }}
          title="Entities Settings"
          onFinish={onFinish}
          settingsVisible={settingsVisible}
          setSettingsVisible={setSettingsVisible}
        />
      )}
    </Styles>
  );
}
